import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
    AccountsColumnToggle,
    AccountsDensity,
    AccountsPlacement,
} from './Shared/AccountsToolbar';
import { usePersistedAccountsViewConfig } from './Shared/usePersistedAccountsViewConfig';

/**
 * EAA-1839: every column of the realm board that may be hidden from the
 * "Hiển thị" popover. `username` is absent ON PURPOSE — it is the row's
 * identity (username + email + the click target that opens the detail), so
 * hiding it would leave a board of attributes belonging to nobody. The org
 * board makes the same call by simply not listing its identity column.
 *
 * FOUR OF THE FIVE START VISIBLE; `last_login` STARTS HIDDEN. That is a
 * reversal, and the reason is width, so it is recorded rather than quietly
 * changed.
 *
 * This block used to read "none of these start hidden", arguing that the realm
 * read is richer than the org one — `GET /api/v1/accounts` returns `placement`,
 * `app_roles` and `last_login` plus a `last_login_available` flag that lets the
 * cell distinguish "Chưa khả dụng" from "Chưa ghi nhận" from a real date, so
 * every column CAN carry real data here, unlike the org board's `source` /
 * `last_login`, which are structurally empty. That reasoning still holds and is
 * why `last_login` is still offered at all.
 *
 * What it missed is that "can carry data" and "earns 92px of a 821px budget" are
 * different questions. Six data columns do not fit this board's available width
 * without squeezing the three that carry the row's actual identity — which is
 * exactly the defect this pass was opened to fix: a status wrapping to five
 * lines, an email clipped to "vthl2002@gmail.co…", an org name clipped
 * mid-word. `last_login` is the cheapest column to drop: every row in this realm
 * renders the same "Chưa ghi nhận" (measured), so it discriminates between no
 * rows, and it is one click away in the "Hiển thị" popover for the operator who
 * wants it — the same trade the org board makes with its own two.
 *
 * See `COLUMN_WIDTHS` in `AccountsListTable.tsx`: the budget is DECLARED for all
 * six so re-enabling this column can never overflow (`.acc-table-scroll` is
 * `overflow-x: hidden`, so overflow means clipped columns, not a scrollbar), and
 * SIZED for the five that show by default, which `table-layout: fixed` widens
 * proportionally with the freed 92px.
 */
const HIDEABLE_COLUMNS = ['email', 'placement', 'app_roles', 'status', 'last_login'] as const;

/** See `HIDEABLE_COLUMNS`' docstring: hidden by default because it renders the
 *  same "Chưa ghi nhận" on every row in this realm and its width is needed by the
 *  columns that do distinguish rows. Still offered in the "Hiển thị" popover. */
const DEFAULT_HIDDEN_COLUMNS: readonly RealmAccountColumnKey[] = ['last_login'];
export type RealmAccountColumnKey = (typeof HIDEABLE_COLUMNS)[number];

/**
 * EAA-1839: `portal:accountsView:realm-accounts` — DISTINCT from the org
 * board's `portal:accountsView:scoped-accounts`. One entry per TABLE, not per
 * screen: the two boards have different column sets, so a shared key would let
 * each board hide columns the other does not have and silently drop them on the
 * next read (rule 3 in `usePersistedAccountsViewConfig`).
 */
const VIEW_CONFIG_KEY = 'realm-accounts';

/**
 * EAA-1839 (realm-board parity pass): the PRESENTATION state of the realm
 * accounts board — rail quick filter, visible columns, row density.
 *
 * Split from `useRealmAccountsAdapter` for the same reason
 * `useScopedAccountViews` is split from `useScopedAccounts`: that hook owns
 * what the SERVER is asked (search, filters, paging, sort, selection) and is
 * already long, and none of the state here reaches a request.
 *
 * WHY THIS IS NOT `useScopedAccountViews`, i.e. what is genuinely per-screen
 * rather than shared:
 *  - the column set (`HIDEABLE_COLUMNS`) and its i18n keys — the two boards
 *    list different entities (a Keycloak identity vs. an `Assignment` grant);
 *  - the DEFAULT visibility, which follows each endpoint's contract (see
 *    `HIDEABLE_COLUMNS` above);
 *  - the storage key;
 *  - the tab strip: the org board's tabs double as saved views over the same
 *    query (`issue=no_level`), the realm board has only `accounts | audit` and
 *    keeps that state in the page.
 * What IS shared is the persistence mechanism, which is why that lives in
 * `usePersistedAccountsViewConfig` (parameterised by key) instead of being
 * copied — see its docstring for the promotion it is still waiting on.
 *
 * `placement` IS NOW HERE, and the previous pass's refusal is worth reading
 * before anyone removes it again. This docstring used to say it was
 * "DELIBERATELY ABSENT", on two grounds: the toolbar row is at capacity at
 * 1366px, and the switch's only payoff is ~236px of table width the realm board
 * did not need. The user asked for it on 2026-08-24, and both grounds turn out
 * not to argue against it:
 *  - The control does NOT go in the toolbar row. It is the third section of the
 *    "Hiển thị" popover, beside "Cột" and "Mật độ" (`AccountsToolbar`'s
 *    `placement` prop), so the row's capacity is irrelevant.
 *  - "Does not need the width" was measured before this pass found the board
 *    clipping emails and org names. 236px is roughly a THIRD of the 821px the six
 *    data columns share; reclaiming it is the single largest width lever the
 *    board has.
 * Default stays `'sidebar'` — frame 35's only drawn layout, and the layout the
 * `COLUMN_WIDTHS` budget is measured against.
 */
export function useRealmAccountViews() {
    const { t } = useTranslation();
    const [railQuery, setRailQuery] = useState('');
    const [density, setDensityState] = useState<AccountsDensity>('comfortable');
    const [placement, setPlacementState] = useState<AccountsPlacement>('sidebar');
    const [hiddenColumns, setHiddenColumns] = useState<Set<RealmAccountColumnKey>>(
        () => new Set<RealmAccountColumnKey>(DEFAULT_HIDDEN_COLUMNS)
    );

    /**
     * EAA-1839: PRECEDENCE, stated once because it is easy to get backwards — a
     * STORED value always wins over the seeds above, which only apply when
     * nothing is stored yet. That is expressed by ORDER, not a branch: the seed
     * is the `useState` initial value, and the reconciliation effect below only
     * ever OVERWRITES it once `loadState` resolves to a real config; it never
     * re-applies a seed on top of a loaded value. `reconciled` gates the write
     * effect so reconciliation always lands in state BEFORE anything is written
     * back — without it the first render's seed would overwrite a just-loaded
     * value the instant the write effect fired.
     */
    const { loadState, persist } = usePersistedAccountsViewConfig(VIEW_CONFIG_KEY, HIDEABLE_COLUMNS);
    const [reconciled, setReconciled] = useState(false);

    useEffect(() => {
        if (loadState === 'pending') return;
        if (loadState !== 'empty') {
            setHiddenColumns(new Set(loadState.hiddenColumns as RealmAccountColumnKey[]));
            setDensityState(loadState.density);
            /* `placement` is optional on the stored payload — a payload written
               before this board offered the choice has none, and the seed above
               must survive that rather than being overwritten with `undefined`. */
            if (loadState.placement) setPlacementState(loadState.placement);
        }
        setReconciled(true);
    }, [loadState]);

    useEffect(() => {
        if (!reconciled) return;
        persist({ hiddenColumns: [...hiddenColumns], density, placement });
    }, [reconciled, hiddenColumns, density, placement, persist]);

    const setDensity = useCallback((next: AccountsDensity) => setDensityState(next), []);
    const setPlacement = useCallback((next: AccountsPlacement) => setPlacementState(next), []);

    const COLUMN_LABEL_KEYS: Record<RealmAccountColumnKey, string> = {
        email: 'console.accounts.table.columnEmail',
        placement: 'console.accounts.table.columnPlacement',
        app_roles: 'console.accounts.table.columnAppRoles',
        status: 'console.accounts.table.columnStatus',
        last_login: 'console.accounts.table.columnLastLogin',
    };

    const columnItems = useMemo<AccountsColumnToggle[]>(
        () =>
            HIDEABLE_COLUMNS.map((key) => ({
                key,
                label: t(COLUMN_LABEL_KEYS[key]),
                visible: !hiddenColumns.has(key),
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t, hiddenColumns]
    );

    const toggleColumn = useCallback((key: string) => {
        setHiddenColumns((prev) => {
            const next = new Set(prev);
            if (next.has(key as RealmAccountColumnKey)) next.delete(key as RealmAccountColumnKey);
            else next.add(key as RealmAccountColumnKey);
            return next;
        });
    }, []);

    const isColumnVisible = useCallback(
        (key: RealmAccountColumnKey) => !hiddenColumns.has(key),
        [hiddenColumns]
    );

    return {
        railQuery,
        setRailQuery,
        density,
        setDensity,
        placement,
        setPlacement,
        columnItems,
        toggleColumn,
        isColumnVisible,
    };
}

export type RealmAccountViewsVM = ReturnType<typeof useRealmAccountViews>;
